import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetEmployees,
  GetAvailableEmployees,
  CreateEmployee,
  UpdateEmployee,
  ToggleEmployeeStatus,
  GetUnlinkedEmployees,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const employeeQueryKeys = {
  employees: (params?: Parameters<typeof GetEmployees>[0]) =>
    ["employees", params] as const,
  availableEmployees: () => ["availableEmployees"] as const,
  unlinkedEmployees: () => ["unlinkedEmployees"] as const,
};

export const invalidateEmployeeQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["employees"] });
  queryClient.invalidateQueries({ queryKey: ["availableEmployees"] });
};

export const useEmployeesQuery = (
  params: Parameters<typeof GetEmployees>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: employeeQueryKeys.employees(params),
    queryFn: async () => await GetEmployees(params),
  });
};

export const useEmployeesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetEmployees>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: employeeQueryKeys.employees(params),
        queryFn: async () => await GetEmployees(params),
      }),
  });
};

export const useAvailableEmployeesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: employeeQueryKeys.availableEmployees(),
    queryFn: async () => unwrapApiResponse(await GetAvailableEmployees()),
  });
};

export const useAvailableEmployeesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: employeeQueryKeys.availableEmployees(),
        queryFn: async () => unwrapApiResponse(await GetAvailableEmployees()),
      }),
  });
};

export const useCreateEmployeeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateEmployee>[0]) =>
      CreateEmployee(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateEmployeeQueries(queryClient);
      }
    },
  });
};

export const useUpdateEmployeeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateEmployee>[0]) =>
      UpdateEmployee(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateEmployeeQueries(queryClient);
      }
    },
  });
};

export const useToggleEmployeeStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleEmployeeStatus>[0];
      status: Parameters<typeof ToggleEmployeeStatus>[1];
    }) => ToggleEmployeeStatus(payload.id, payload.status),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateEmployeeQueries(queryClient);
      }
    },
  });
};

export const useEmployeeService = (config?: {
  fetchEmployeesParams?: Parameters<typeof GetEmployees>[0];
  fetchEmployeesEnabled?: boolean;
  fetchAvailableEmployeesEnabled?: boolean;
}) => {
  const employeesQuery = useEmployeesQuery(config?.fetchEmployeesParams, {
    enabled:
      config?.fetchEmployeesEnabled !== false &&
      config?.fetchEmployeesParams !== undefined &&
      config?.fetchEmployeesParams !== null,
  });

  const availableEmployeesQuery = useAvailableEmployeesQuery({
    enabled: config?.fetchAvailableEmployeesEnabled !== false,
  });

  const createEmployeeMutation = useCreateEmployeeMutation();
  const updateEmployeeMutation = useUpdateEmployeeMutation();
  const toggleEmployeeStatusMutation = useToggleEmployeeStatusMutation();

  return {
    employeesQuery,
    availableEmployeesQuery,
    createEmployeeMutation,
    updateEmployeeMutation,
    toggleEmployeeStatusMutation,
  };
};

export const useUnlinkedEmployeesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: employeeQueryKeys.unlinkedEmployees(),
    queryFn: async () => await GetUnlinkedEmployees(),
  });
};